import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaPlus } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../styles.module.css";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { useSelector } from "react-redux";
import { CustomButton, CustomDatePicker } from "../../../../components";

function BookStudyMaterial() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";
  const roleId = Number(user?.role?.id);
  const academic_id = clientData?.academic_year_id || "";

  // Hide add button and action column for roleId 3, 4, 6
  const hideActions = [3, 4, 6].includes(roleId);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);

  // Individual column search states (matching API payload keys)
  const [titleSearch, setTitleSearch] = useState("");
  const [topicSearch, setTopicSearch] = useState("");
  const [subTopicSearch, setSubTopicSearch] = useState("");
  const [subjectSearch, setSubjectSearch] = useState("");
  const [classSearch, setClassSearch] = useState("");
  const [sectionSearch, setSectionSearch] = useState("");
  const [mediumSearch, setMediumSearch] = useState("");
  const [studentNameSearch, setStudentNameSearch] = useState("");
  const [createdDateSearch, setCreatedDateSearch] = useState("");

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Combined filters object to trigger API re-fetch
  const combinedFilters = useMemo(
    () => ({
      title: titleSearch,
      topic: topicSearch,
      sub_topic: subTopicSearch,
      subject: subjectSearch,
      class_id: classSearch,
      section_id: sectionSearch,
      medium: mediumSearch,
      student_name: studentNameSearch,
      created_date: createdDateSearch,
    }),
    [
      titleSearch,
      topicSearch,
      subTopicSearch,
      subjectSearch,
      classSearch,
      sectionSearch,
      mediumSearch,
      studentNameSearch,
      createdDateSearch,
    ],
  );

  // Map of column id -> setter (used to sync cleared filters)
  const filterSetters = useMemo(
    () => ({
      title: setTitleSearch,
      topic: setTopicSearch,
      sub_topic: setSubTopicSearch,
      subject: setSubjectSearch,
      class: setClassSearch,
      section: setSectionSearch,
      medium: setMediumSearch,
      student_name: setStudentNameSearch,
      created_date: setCreatedDateSearch,
    }),
    [],
  );

  // Fetch study material list on mount and when dependencies change
  useEffect(() => {
    if (school_id) {
      fetchStudyMaterial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id, pagination.pageIndex, pagination.pageSize, combinedFilters]);

  // API call to fetch study material list
  const fetchStudyMaterial = async () => {
    setLoading(true);
    try {
      const payload = {
        user_id: user_id,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: "",
        title: titleSearch,
        subject: subjectSearch,
        topic: topicSearch,
        sub_topic: subTopicSearch,
        created_date: createdDateSearch,
        academic_id: academic_id,
      };

      const response = await AxiosInstance.post(
        `/book/student/studyMaterialList`,
        payload,
      );

      if (response?.data?.status) {
        const transformedData = response.data.data.map((item, index) => ({
          id: item.id,
          s_no: pagination.pageIndex * pagination.pageSize + index + 1,
          assessment_id: item.assessment_id || "",
          book_id: item.book_id || "",
          title: item.title || "-",
          topic: item.topic_name || item.topic || "-",
          sub_topic: item.sub_topic_name || item.sub_topic || "-",
          subject: item.subject_name || item.subject || "-",
          class: item.class_name || item.class || "-",
          section: item.section_name || item.section || "-",
          medium: item.medium_name || item.medium || "-",
          student_name: item.student_name || "-",
          created_date: item.created_at
            ? dayjs(item.created_at).format("DD/MM/YYYY")
            : "",
          client_id: item.client_id,
          school_id: item.school_id,
          class_id: item.class_id,
          section_id: item.section_id,
          isActive: item.isActive,
        }));

        setData(transformedData);
        setRowCount(response.data.pagination?.total || transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching study material list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/user/studyMaterialCreate");
  };

  const handleView = (row) => {
    navigate("/user/ViewStudyMaterial", {
      state: {
        ID: row.assessment_id,
        data: row,
      },
    });
  };

  // Sync cleared filters from MRT internal state -> individual search states.
  // When the X (clear) button is clicked, MRT removes the entry from columnFilters
  // (or sets its value to empty). We detect that here and reset the matching
  // search state, which triggers the API refetch with an empty payload.
  const handleColumnFiltersChange = (updaterOrValue) => {
    const newFilters =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnFilters)
        : updaterOrValue;

    Object.keys(filterSetters).forEach((columnId) => {
      const oldFilter = columnFilters.find((f) => f.id === columnId);
      const newFilter = newFilters.find((f) => f.id === columnId);

      const oldValue = oldFilter?.value ?? "";
      const newValue = newFilter?.value ?? "";

      // Filter was cleared (had a value, now empty/removed)
      if (oldValue !== "" && newValue === "") {
        filterSetters[columnId]("");
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }
    });

    setColumnFilters(newFilters);
  };

  // Generic Enter-key handler factory for each column filter
  const makeFilterKeyDownHandler = (setter) => (e) => {
    if (e.key === "Enter") {
      const inputValue = e.target.value || "";
      setter(inputValue);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };
  // ==================== SINGLE DATE FILTER ====================
  // Single date picker for the Created Date column.
  // On change, the date is formatted as "YYYY-MM-DD" (e.g. "2026-05-07")
  // and pushed into createdDateSearch -> sent in the API payload.
  const SingleDateFilter = ({ column, translations: t, onDateChange }) => {
    const filterValue = column.getFilterValue();
    const [localDate, setLocalDate] = useState(filterValue || "");

    // Keep local state in sync if the external filter is cleared from outside
    useEffect(() => {
      setLocalDate(filterValue || "");
    }, [filterValue]);

    const dateValue = localDate ? dayjs(localDate) : null;

    const handleChange = (dayjsDate) => {
      const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
      setLocalDate(formatted);
      // Keep MRT's columnFilters in sync (so chips/badges update)
      column.setFilterValue(formatted || undefined);

      if (typeof onDateChange === "function") {
        onDateChange(formatted);
      }
    };

    return (
      <CustomDatePicker
        name={`${column.id}_date`}
        value={dateValue}
        onChange={handleChange}
        placeholder={t?.labels?.createdDate || "Created Date"}
        format="DD/MM/YYYY"
        size="sm"
        isClearable={true}
      />
    );
  };
  // Columns matching the screenshot
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: "title",
        header: translations.labels.title || "Title",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: "Title",
          onKeyDown: makeFilterKeyDownHandler(setTitleSearch),
        },
      },
      {
        accessorKey: "topic",
        header: translations.labels.topic || "Topic",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: "All Topic",
          onKeyDown: makeFilterKeyDownHandler(setTopicSearch),
        },
      },
      {
        accessorKey: "sub_topic",
        header: translations.labels.subTopic || "Sub Topic",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: "All Sub Topic",
          onKeyDown: makeFilterKeyDownHandler(setSubTopicSearch),
        },
      },
      {
        accessorKey: "subject",
        header: translations.labels.subject || "Subject",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: "Subject",
          onKeyDown: makeFilterKeyDownHandler(setSubjectSearch),
        },
      },

      {
        accessorKey: "created_date",
        header: translations.labels.createdDate || "Created Date",
        enableSorting: true,
        enableColumnFilter: true,
        Filter: ({ column }) => (
          <SingleDateFilter
            column={column}
            translations={translations}
            onDateChange={(formatted) => {
              setCreatedDateSearch(formatted);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          />
        ),
        Cell: ({ cell }) => cell.getValue() || "-",
        size: 200,
      },
      {
        id: "actions",
        header: translations.labels.actions || "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title="View"
          >
            <FaEye />
          </button>
        ),
      },
    ];

    // Only add actions column if role is NOT 3, 4, or 6

    return baseColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideActions, translations]);

  return (
    <div className={styles.container}>
      {/* Actions - Hide ADD button for roleId 3, 4, 6 */}
      {!hideActions && (
        <div className={styles.actions}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            fullWidth={false}
            onClick={handleAdd}
          >
            <FaPlus style={{ marginRight: "8px" }} />
            {translations.labels.add}
          </CustomButton>
        </div>
      )}

      {/* Table - no global search, only column filters */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        enableColumnFilters={true}
        enableGlobalFilter={false}
        enableSearch={false}
      />
    </div>
  );
}

export default BookStudyMaterial;
